import { useMemo } from "react"

import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useDemandHierarchy, useLevelLabel } from "~/components/demand-2/useDemandHierarchy"
import { useChannels } from "~/hooks/useChannels"
import { api } from "~/utils/api"
import type { FramingSlice } from "~/utils/demandFraming"
import { isSalesPlanningLevel } from "~/utils/demandHierarchy"
import {
  coverageBySlice,
  coverageOf,
  plannableSlices,
  salesLevelsLabel,
  type SliceCoverage,
  sliceParts,
  sliceSentenceParts,
} from "~/utils/demandInsightSlice"

const useSalesDimensions = () => {
  const { collectionIds } = useDemand2Scope()
  const { levels } = useDemandHierarchy()
  const salesLevels = useMemo(() => levels.filter(isSalesPlanningLevel), [levels])
  const dims = api.demand2.getDimensions.useQuery(
    { collectionIds },
    { enabled: salesLevels.length > 0 && collectionIds.length > 0 },
  )
  const channels = useChannels()
  const warehouses = api.warehouse.get.useQuery(undefined, { enabled: salesLevels.length > 0 })
  return {
    salesLevels,
    dims: dims.data,
    channels: channels.data,
    warehouses: warehouses.data,
  }
}

/** A slice's parts for a sentence: channel first, names title-cased. Names read as ids until loaded. */
export type SlicePartsOf = (slice: FramingSlice, withoutChannel?: boolean) => string[]

export const useSliceParts = (): SlicePartsOf => {
  const { salesLevels, dims, channels, warehouses } = useSalesDimensions()
  const markets = dims?.markets ?? []
  return (slice, withoutChannel) =>
    sliceSentenceParts(slice, salesLevels, { markets, channels, warehouses }, withoutChannel)
}

/** What one slice is counted as: its deepest sales level, "4 markets" on channel × market. */
export const useSliceCountNoun = (): string => {
  const { salesLevels: sales } = useSalesDimensions()
  const labelFor = useLevelLabel()
  const deepest = sales.at(-1)
  return deepest == null ? "channels" : `${labelFor(deepest).toLowerCase()}s`
}

export type SalesSlices = {
  title: string
  noun: string
  /** `coverage` is absent until its read lands; such a slice is listed as a row. */
  slices: { slice: FramingSlice; label: string; coverage?: SliceCoverage }[]
}

/**
 * The slices an insight answer can be given on: every plannable combination (Market Strategy's), on
 * the planning hierarchy's sales levels. Null until loaded, and when no sales level is configured.
 */
export const useSalesSlices = (): SalesSlices | null => {
  const { collectionIds } = useDemand2Scope()
  const labelFor = useLevelLabel()
  const { salesLevels, dims: data, channels, warehouses } = useSalesDimensions()
  const enabled = salesLevels.length > 0 && collectionIds.length > 0
  const coverage = api.demand2.getSliceCoverage.useQuery({ collectionIds }, { enabled })
  const plannable = api.demand2.getPlannableCells.useQuery(undefined, {
    enabled: salesLevels.length > 0,
  })
  return useMemo(() => {
    if (salesLevels.length === 0 || data == null || plannable.data == null) return null
    const bySlice = coverage.data == null ? null : coverageBySlice(salesLevels, coverage.data)
    const named = salesLevelsLabel(salesLevels, labelFor)
    return {
      title: `By ${named}`,
      noun: named.toLowerCase(),
      slices: plannableSlices(salesLevels, plannable.data.cells)
        .map((slice) => ({
          slice,
          label: sliceParts(slice, salesLevels, {
            markets: data.markets,
            channels,
            warehouses,
          }).join(" · "),
          coverage: bySlice == null ? undefined : coverageOf(bySlice, slice),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }
  }, [data, coverage.data, plannable.data, salesLevels, labelFor, channels, warehouses])
}
